export type EmailBodyFormat = 'plain' | 'html';

export type SendEmailTemplateDefinition = {
  id: string;
  label: string;
  subject: string;
  body: string;
  bodyFormat: EmailBodyFormat;
};

/** Built-in samples for Send Email — applied into subject/body/format; users edit after pick. */
export const SEND_EMAIL_SAMPLE_TEMPLATES: readonly SendEmailTemplateDefinition[] = [
  {
    id: 'tpl-report-ready',
    label: 'Report ready',
    subject: 'Your report is ready',
    body:
      'Hi {{ lead.name }},\n\nYour analysis is ready. You can review it in the portal.\n\nBest regards,\n{{ workflow.id }}',
    bodyFormat: 'plain',
  },
  {
    id: 'tpl-lead-followup',
    label: 'Lead follow-up',
    subject: 'Following up — {{ lead.company }}',
    body:
      'Hi {{ lead.name }},\n\nThanks for your interest. I wanted to follow up on our last conversation.\n\nIf you have a few minutes this week, reply with a time that works for you.\n\nRegards',
    bodyFormat: 'plain',
  },
  {
    id: 'tpl-internal-alert',
    label: 'Internal alert',
    subject: '[Alert] Workflow {{ workflow.id }} — action required',
    body:
      'Team,\n\nThe workflow run needs attention.\n\nDetails:\n- Trigger: {{ trigger.timestamp }}\n- Last output: {{ previous.output }}\n\nPlease review the run log.',
    bodyFormat: 'plain',
  },
  {
    id: 'tpl-welcome-html',
    label: 'Welcome (HTML)',
    subject: 'Welcome, {{ lead.name }}',
    body: `<!DOCTYPE html>
<html><body style="font-family:system-ui,sans-serif;line-height:1.5;color:#111">
  <p>Hi {{ lead.name }},</p>
  <p>Welcome aboard. Your account for <strong>{{ lead.email }}</strong> is active.</p>
  <p>If you did not expect this message, you can ignore it.</p>
  <p style="color:#666;font-size:12px">Sent by workflow {{ workflow.id }}</p>
</body></html>`,
    bodyFormat: 'html',
  },
  {
    id: 'tpl-task-assigned',
    label: 'Task assigned',
    subject: 'You have been assigned a task',
    body:
      'Hello,\n\nA new task was assigned to you from workflow {{ workflow.id }}.\n\nSummary: {{ previous.output }}\n\nDue: please check your task board.\n\nThanks',
    bodyFormat: 'plain',
  },
] as const;

export function getSendEmailTemplateById(id: string): SendEmailTemplateDefinition | undefined {
  return SEND_EMAIL_SAMPLE_TEMPLATES.find((t) => t.id === id);
}

export type SendEmailTemplatePatch = {
  emailTemplateId: string;
  emailSubject: string;
  emailBody: string;
  emailBodyFormat: EmailBodyFormat;
};

export function patchFromSendEmailTemplate(templateId: string): SendEmailTemplatePatch | null {
  const t = getSendEmailTemplateById(templateId);
  if (!t) return null;
  return {
    emailTemplateId: t.id,
    emailSubject: t.subject,
    emailBody: t.body,
    emailBodyFormat: t.bodyFormat,
  };
}
